import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchDeliveryAddressById, updateDeliveryAddress } from '../services/api.ts';
import { ArrowLeft, MapPin, Home, Loader, Star } from 'lucide-react';
import './EditDeliveryAddress.css';

const EditDeliveryAddress: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    user_id: '',
    address_line_1: '',
    address_line_2: '',
    town: '',
    state_id: 0,
    postcode: '',
    country_id: 0,
    is_default: false
  });

  useEffect(() => {
    const loadAddress = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await fetchDeliveryAddressById(id);
        setFormData({
          user_id: data.user_id || '',
          address_line_1: data.address_line_1 || '',
          address_line_2: data.address_line_2 || '',
          town: data.town || '',
          state_id: data.state_id || 0,
          postcode: data.postcode || '',
          country_id: data.country_id || 0,
          is_default: data.is_default || false
        });
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load delivery address');
        console.error('Error loading delivery address:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAddress();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      setError(null);
      await updateDeliveryAddress(id, formData);
      navigate('/dashboard/delivery-addresses');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.error 
        || err.message 
        || 'Failed to update delivery address';
      setError(errorMessage);
      console.error('Update error:', err.response?.data);
    }
  };

  if (loading) {
    return (
      <div className="edit-delivery-address">
        <div className="loading-container">
          <Loader className="loading-spinner-icon" size={40} />
          <p>Loading delivery address...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-delivery-address">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/dashboard/delivery-addresses')}>
          <ArrowLeft size={20} />
          Back to Delivery Addresses
        </button>
        <h2>Edit Delivery Address</h2>
      </div>

      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-section">
            <h3 className="section-title">Address Information</h3>
            
            <div className="form-group">
              <label>User ID *</label>
              <input
                type="text"
                value={formData.user_id}
                onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                placeholder="Enter user ID"
                required
              />
              <p className="field-hint">The ID of the user this address belongs to</p>
            </div>

            <div className="form-group">
              <label>Address Line 1 *</label>
              <input
                type="text"
                value={formData.address_line_1}
                onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                placeholder="Street address, P.O. box, company name"
                required
              />
            </div>

            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                value={formData.address_line_2}
                onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                placeholder="Apartment, suite, unit, building, floor, etc."
              />
            </div>

            <div className="form-group">
              <label>Town/City *</label>
              <input
                type="text"
                value={formData.town}
                onChange={(e) => setFormData({ ...formData, town: e.target.value })}
                placeholder="Enter town or city"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>State ID *</label>
                <input
                  type="number"
                  value={formData.state_id}
                  onChange={(e) => setFormData({ ...formData, state_id: parseInt(e.target.value) })}
                  placeholder="State ID"
                  required
                />
              </div>

              <div className="form-group">
                <label>Country ID *</label>
                <input
                  type="number"
                  value={formData.country_id}
                  onChange={(e) => setFormData({ ...formData, country_id: parseInt(e.target.value) })}
                  placeholder="Country ID"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Postcode *</label>
              <input
                type="text"
                value={formData.postcode}
                onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                placeholder="Enter postcode/ZIP code"
                required
              />
            </div>

            <div className="form-switches">
              <div className="switch-group">
                <div className="switch-content">
                  <label className="switch-label">Set as Default Address</label>
                  <p className="switch-description">Make this the default delivery address</p>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Preview</h3>
            
            <div className="preview-card">
              <div className="preview-header">
                <div className="preview-icon">
                  <Home size={24} />
                </div>
                {formData.is_default && (
                  <div className="preview-default-badge">
                    <Star size={14} fill="currentColor" />
                    Default
                  </div>
                )}
              </div>

              <div className="preview-content">
                <div className="preview-address-lines">
                  <p className="preview-line-1">
                    {formData.address_line_1 || 'Address Line 1'}
                  </p>
                  {formData.address_line_2 && (
                    <p className="preview-line-2">{formData.address_line_2}</p>
                  )}
                </div>

                <div className="preview-details">
                  <div className="preview-detail-item">
                    <MapPin size={16} />
                    <span>{formData.town || 'Town/City'}</span>
                  </div>
                  <div className="preview-detail-item">
                    <span className="preview-label">Postcode:</span>
                    <span className="preview-value">{formData.postcode || 'N/A'}</span>
                  </div>
                  <div className="preview-detail-item">
                    <span className="preview-label">User ID:</span>
                    <span className="preview-value">
                      {formData.user_id ? `${formData.user_id.substring(0, 8)}...` : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="preview-meta">
                  <span className="preview-meta-badge">State ID: {formData.state_id || 'N/A'}</span>
                  <span className="preview-meta-badge">Country ID: {formData.country_id || 'N/A'}</span>
                </div>
              </div>
            </div>

            <div className="info-box">
              <h4>📍 Address Guidelines</h4>
              <ul>
                <li>Ensure all required fields are filled</li>
                <li>Use accurate state and country IDs</li>
                <li>Verify postcode format is correct</li>
                <li>Only one default address per user</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={() => navigate('/dashboard/delivery-addresses')}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditDeliveryAddress;
